import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, List, CalendarRange, GripVertical, Sparkles, Loader2 } from "lucide-react";
import { Card, PageHeader, StatusPill, Btn, Empty, inputCls } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";

const PLATFORMS = ["Instagram", "Facebook", "TikTok", "LinkedIn"];
const TYPES = ["Post", "Story", "Reel", "Promotion"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const startOfWeek = (d) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7));
  return copy;
};

export default function ContentCalendar() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("month");
  const [platformFilter, setPlatformFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState("");
  const [overDay, setOverDay] = useState("");
  const [planCount, setPlanCount] = useState(12);
  const [planning, setPlanning] = useState(false);
  const [planSummary, setPlanSummary] = useState("");

  const generateMonth = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post("/engine/month-plan", {
        month: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`,
        posts: planCount,
        platforms: platformFilter.length > 0 ? platformFilter : ["Instagram"],
      });
      setPlanSummary(data.plan_summary || "");
      setItems((list) => [...list, ...data.items]);
      toast.success(`${data.items.length} posts planned for ${data.month}.`);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setPlanning(false);
    }
  };

  useEffect(() => {
    api
      .get("/content")
      .then((r) => setItems(r.data))
      .catch((e) => {
        console.error("Could not load your calendar", e);
        toast.error("We couldn't load your calendar.");
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      items.filter(
        (i) =>
          (platformFilter.length === 0 || platformFilter.includes(i.platform)) &&
          (typeFilter.length === 0 || typeFilter.includes(i.content_type))
      ),
    [items, platformFilter, typeFilter]
  );

  const byDate = useMemo(() => {
    const map = {};
    filtered.forEach((i) => {
      if (!i.post_date) return;
      map[i.post_date] = map[i.post_date] || [];
      map[i.post_date].push(i);
    });
    return map;
  }, [filtered]);

  const unscheduled = useMemo(
    () => filtered.filter((i) => !i.post_date && i.status !== "Published"),
    [filtered]
  );

  const move = async (id, date) => {
    const item = items.find((i) => i.id === id);
    if (!item || item.post_date === date) return;
    if (item.status === "Published") {
      toast.error("This post has already been published, so it can't be moved.");
      return;
    }
    const previous = items;
    setItems((list) => list.map((i) => (i.id === id ? { ...i, post_date: date, month: date.slice(0, 7) } : i)));
    try {
      const { data } = await api.patch(`/content/${id}/date`, { post_date: date });
      setItems((list) => list.map((i) => (i.id === id ? data : i)));
      toast.success(`Moved to ${new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}.`);
    } catch (e) {
      setItems(previous);
      toast.error(errMessage(e));
    }
  };

  const onDrop = (date) => (e) => {
    e.preventDefault();
    setOverDay("");
    const id = dragId || e.dataTransfer.getData("text/plain");
    setDragId("");
    if (id) move(id, date);
  };

  const Chip = ({ item, wide = false }) => (
    <div
      draggable={item.status !== "Published"}
      onDragStart={(e) => {
        setDragId(item.id);
        e.dataTransfer.setData("text/plain", item.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={() => setDragId("")}
      data-testid={`calendar-chip-${item.id}`}
      title={`${item.platform} · ${item.content_type} — ${item.status}`}
      className={`group flex select-none items-center gap-1 truncate rounded px-1 py-0.5 text-[10px] font-semibold ${
        wide ? "border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700" : ""
      } ${
        item.status === "Published"
          ? "bg-emerald-50 text-emerald-800"
          : "cursor-grab bg-[#FFF3EF] text-[#B93615] active:cursor-grabbing"
      } ${wide && item.status !== "Published" ? "bg-white text-slate-700" : ""} ${
        dragId === item.id ? "opacity-40" : ""
      }`}
    >
      {item.status !== "Published" && (
        <GripVertical className="h-2.5 w-2.5 shrink-0 opacity-50" aria-hidden="true" />
      )}
      <span className="truncate">
        {wide
          ? `${item.platform} · ${item.content_type} — ${(item.caption || item.title || "").slice(0, 40)}`
          : `${item.platform?.slice(0, 2)} · ${item.content_type}`}
      </span>
    </div>
  );

  const DayCell = ({ date, label, sub, minHeight = "min-h-[86px]" }) => {
    const key = iso(date);
    const entries = byDate[key] || [];
    return (
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOverDay(key);
        }}
        onDragLeave={() => setOverDay((d) => (d === key ? "" : d))}
        onDrop={onDrop(key)}
        data-testid={`calendar-day-${label}`}
        data-date={key}
        className={`${minHeight} rounded-lg border p-1.5 text-left transition-colors ${
          overDay === key ? "border-[#F95738] bg-[#FFF3EF]" : "border-slate-200"
        }`}
      >
        <span className="text-[11px] font-semibold text-slate-400">
          {label}
          {sub ? <span className="ml-1 font-normal text-slate-300">{sub}</span> : null}
        </span>
        <div className="mt-1 space-y-1">
          {entries.map((e) => (
            <Chip key={e.id} item={e} />
          ))}
        </div>
      </div>
    );
  };

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (first.getDay() + 6) % 7;
  const weekStart = startOfWeek(cursor);

  const shift = (amount) => {
    if (view === "week") {
      const next = new Date(cursor);
      next.setDate(next.getDate() + amount * 7);
      setCursor(next);
    } else {
      setCursor(new Date(year, month + amount, 1));
    }
  };

  const title =
    view === "week"
      ? `Week of ${weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`
      : first.toLocaleString("en-GB", { month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="content-calendar-page">
      <PageHeader
        title="Content Calendar"
        body="Drag any planned post to another day to reschedule it. Published posts stay where they are."
        action={
          <div className="flex flex-wrap gap-2">
            <Btn variant={view === "month" ? "dark" : "outline"} testId="calendar-view-month" onClick={() => setView("month")}>
              <CalendarDays className="h-4 w-4" /> Month
            </Btn>
            <Btn variant={view === "week" ? "dark" : "outline"} testId="calendar-view-week" onClick={() => setView("week")}>
              <CalendarRange className="h-4 w-4" /> Week
            </Btn>
            <Btn variant={view === "list" ? "dark" : "outline"} testId="calendar-view-list" onClick={() => setView("list")}>
              <List className="h-4 w-4" /> List
            </Btn>
          </div>
        }
      />

      <Card testId="calendar-month-plan">
        <p className="font-display text-lg font-semibold text-slate-900">Generate my month</p>
        <p className="mt-2 text-sm text-slate-600">
          One click builds a full month of varied posts from your content pillars, spread across the days. Everything
          arrives as a draft for you to review.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="text-xs font-semibold text-slate-500">
            How many posts?
            <select
              value={planCount}
              onChange={(e) => setPlanCount(Number(e.target.value))}
              data-testid="month-plan-count"
              className={`${inputCls} mt-1 max-w-[8rem]`}
            >
              {[8, 12, 16, 20].map((n) => (
                <option key={n} value={n}>{n} posts</option>
              ))}
            </select>
          </label>
          <Btn onClick={generateMonth} disabled={planning} testId="month-plan-button">
            {planning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {planning ? "Planning your month…" : "Generate my month"}
          </Btn>
        </div>
        {planSummary && (
          <p className="mt-4 rounded-2xl bg-[#FFF3EF] p-3 text-sm text-slate-700" data-testid="month-plan-summary">
            {planSummary}
          </p>
        )}
      </Card>

      <Card testId="calendar-filters">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Filters</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {PLATFORMS.map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`calendar-filter-${p.toLowerCase()}`}
              onClick={() =>
                setPlatformFilter((list) => (list.includes(p) ? list.filter((v) => v !== p) : [...list, p]))
              }
              aria-pressed={platformFilter.includes(p)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                platformFilter.includes(p) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"
              }`}
            >
              {p}
            </button>
          ))}
          <span className="mx-1 h-6 w-px bg-slate-200" aria-hidden="true" />
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`calendar-filter-${t.toLowerCase()}`}
              onClick={() => setTypeFilter((list) => (list.includes(t) ? list.filter((v) => v !== t) : [...list, t]))}
              aria-pressed={typeFilter.includes(t)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                typeFilter.includes(t) ? "border-[#F95738] bg-[#F95738] text-white" : "border-slate-200 text-slate-600"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Card testId="calendar-loading">Loading calendar…</Card>
      ) : view === "list" ? (
        filtered.length === 0 ? (
          <Empty
            testId="calendar-empty"
            title="Nothing planned yet"
            body="Your content items appear here once they are ready for review."
          />
        ) : (
          <div className="space-y-3" data-testid="calendar-list">
            {filtered.map((i) => (
              <Card key={i.id} testId={`calendar-list-item-${i.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">
                      {i.platform} · {i.content_type}
                    </p>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-600">{i.caption || i.title}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="date"
                      value={i.post_date || ""}
                      disabled={i.status === "Published"}
                      onChange={(e) => e.target.value && move(i.id, e.target.value)}
                      data-testid={`calendar-list-date-${i.id}`}
                      className={`${inputCls} max-w-[11rem]`}
                    />
                    <StatusPill status={i.status} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : (
        <Card testId="calendar-grid">
          <div className="flex items-center justify-between gap-3">
            <Btn variant="outline" testId="calendar-prev" onClick={() => shift(-1)}>
              Previous
            </Btn>
            <p className="font-display text-base font-semibold text-slate-900 sm:text-lg">{title}</p>
            <Btn variant="outline" testId="calendar-next" onClick={() => shift(1)}>
              Next
            </Btn>
          </div>

          <div className="mt-5 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-slate-400">
            {DAY_LABELS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          {view === "month" ? (
            <div className="mt-1 grid grid-cols-7 gap-1">
              {Array.from({ length: offset }).map((_, i) => (
                <div key={`pad-${i}`} className="min-h-[86px] rounded-lg bg-slate-50/60" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => (
                <DayCell key={i + 1} date={new Date(year, month, i + 1)} label={String(i + 1)} />
              ))}
            </div>
          ) : (
            <div className="mt-1 grid grid-cols-7 gap-1">
              {Array.from({ length: 7 }).map((_, i) => {
                const date = new Date(weekStart);
                date.setDate(weekStart.getDate() + i);
                return (
                  <DayCell
                    key={iso(date)}
                    date={date}
                    label={String(date.getDate())}
                    sub={date.toLocaleDateString("en-GB", { month: "short" })}
                    minHeight="min-h-[220px]"
                  />
                );
              })}
            </div>
          )}

          <p className="mt-4 text-xs text-slate-500" data-testid="calendar-drag-hint">
            Tip: drag a post onto another day to move it. On a phone, use List view and change the date there.
          </p>

          {unscheduled.length > 0 && (
            <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-[#FAFAFA] p-4" data-testid="calendar-unscheduled">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Not on the calendar yet ({unscheduled.length})
              </p>
              <p className="mt-1 text-xs text-slate-500">Drag any of these onto a day above to plan it.</p>
              <div className="mt-3 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {unscheduled.slice(0, 12).map((i) => (
                  <Chip key={i.id} item={i} wide />
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
